import ThemedSelect from "../shared/ThemedSelect.jsx";
import { useEffect, useState } from "react";
import { Badge, CloseButton, Form } from "react-bootstrap";
import { listSystemSettings, listUsers, setSystemSetting } from "../../services/apiClient.js";

// System Data → System Settings: app-level toggles from config/systemSettings.js.
// Renders exactly what the server returns — admins see admin-scope settings;
// the System Owner also gets the owner section (owner-scoped keys are never
// sent to anyone else, so their existence stays private). Adding a setting =
// one registry entry; this component needs no changes.


// A "users" setting: the stored value is a comma-separated username list, but
// nobody should have to TYPE usernames (Robert 2026-09-02: "free text even for
// system owner is not nice"). Chips with an x to remove; a dropdown of active
// users to add. Every change saves immediately, like the switches.
function UsersSettingRow({ s, onSave, busy }) {
  const [allUsers, setAllUsers] = useState(null);
  useEffect(() => {
    listUsers({ activeOnly: true }).then(setAllUsers).catch(() => setAllUsers([]));
  }, []);

  const chosen = String(s.value || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
  const lower = new Set(chosen.map((x) => x.toLowerCase()));
  const addable = (allUsers || []).filter((u) => !lower.has(String(u.username).toLowerCase()));

  return (
    <div className="d-flex align-items-start justify-content-between column-gap-4 py-2 border-bottom">
      <div>
        <div className="fw-semibold">{s.label}</div>
        <div className="small text-muted">{s.help}</div>
      </div>
      <div style={{ maxWidth: 340 }} className="mt-1">
        <div className="d-flex flex-wrap gap-1 mb-1">
          {chosen.map((name) => (
            <Badge key={name} bg="light" text="dark" className="border d-inline-flex align-items-center gap-1 fw-normal">
              {name}
              <CloseButton
                style={{ fontSize: "0.55rem" }}
                aria-label={`Remove ${name}`}
                disabled={busy}
                onClick={() => onSave(chosen.filter((n) => n !== name).join(", "))}
              />
            </Badge>
          ))}
          {chosen.length === 0 ? <span className="small text-muted">Nobody</span> : null}
        </div>
        <ThemedSelect
          size="sm"
          value=""
          disabled={busy || allUsers === null}
          onChange={(e) => {
            if (e.target.value) onSave([...chosen, e.target.value].join(", "));
          }}
        >
          <option value="">{allUsers === null ? "Loading users…" : "Add a user…"}</option>
          {addable.map((u) => (
            <option key={u.id} value={u.username}>{u.full_name || u.username}</option>
          ))}
        </ThemedSelect>
      </div>
    </div>
  );
}

function SettingRow({ s, onToggle, busyKey }) {
  return (
    <div className="d-flex align-items-start justify-content-between column-gap-4 py-2 border-bottom">
      <div>
        <div className="fw-semibold">{s.label}</div>
        <div className="small text-muted">{s.help}</div>
      </div>
      {/* A text setting (a URL, a name) edits in a box and saves on blur —
          the switch convention cannot carry an address. */}
      {s.type === "text" ? (
        <Form.Control
          size="sm"
          className="mt-1"
          style={{ maxWidth: 320 }}
          defaultValue={s.value || ""}
          disabled={busyKey === s.key}
          placeholder="(not set)"
          onBlur={(e) => {
            if (e.target.value.trim() !== (s.value || "")) onToggle(s.key, e.target.value.trim());
          }}
        />
      ) : (
        <Form.Check
          type="switch"
          id={`sys-setting-${s.key}`}
          className="mt-1"
          checked={!!s.value}
          disabled={busyKey === s.key}
          onChange={(e) => onToggle(s.key, e.target.checked)}
        />
      )}
    </div>
  );
}

function SystemSettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    listSystemSettings()
      .then(setSettings)
      .catch(() => setError("Unable to load system settings."));
  }, []);

  async function toggle(key, value) {
    setBusyKey(key);
    setError("");
    try {
      await setSystemSetting(key, value);
      setSettings((cur) => cur.map((s) => (s.key === key ? { ...s, value } : s)));
    } catch (err) {
      setError(err.response?.data?.error || "Unable to change the setting.");
    } finally {
      setBusyKey(null);
    }
  }

  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!settings) return <p className="text-muted">Loading…</p>;

  const adminSettings = settings.filter((s) => s.scope === "admin");
  const ownerSettings = settings.filter((s) => s.scope === "owner");

  return (
    <div style={{ maxWidth: 720 }}>
      <h5 className="mb-2">System Settings</h5>
      {adminSettings.map((s) => (
        s.type === "users"
          ? <UsersSettingRow key={s.key} s={s} onSave={(v) => toggle(s.key, v)} busy={busyKey === s.key} />
          : <SettingRow key={s.key} s={s} onToggle={toggle} busyKey={busyKey} />
      ))}
      {adminSettings.length === 0 ? <p className="text-muted">No settings.</p> : null}

      {/* Only the System Owner ever receives these — the section simply doesn't
          exist for anyone else. */}
      {ownerSettings.length > 0 ? (
        <>
          <h6 className="mt-4 mb-1 text-uppercase" style={{ fontSize: "0.72rem", letterSpacing: "0.06em" }}>
            System Owner
          </h6>
          <div className="border border-dark rounded px-3">
            {ownerSettings.map((s) => (
              s.type === "users"
                ? <UsersSettingRow key={s.key} s={s} onSave={(v) => toggle(s.key, v)} busy={busyKey === s.key} />
                : <SettingRow key={s.key} s={s} onToggle={toggle} busyKey={busyKey} />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

export default SystemSettingsPanel;
